import React, { useState, useEffect } from 'react';
import { Upload, Image as ImageIcon } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { toast } from 'sonner';

const HostelSettings = () => {
  const [hostelId, setHostelId] = useState('');
  const [settings, setSettings] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    contact_info: '',
    student_id_prefix: 'STU',
    id_start_number: 1001,
    logo_filename: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    // Get hostel ID from user context
    const user = JSON.parse(localStorage.getItem('hostelconnect_user') || '{}');
    if (user.hostel_id) {
      setHostelId(user.hostel_id);
      fetchSettings(user.hostel_id);
    }
  }, []);

  const fetchSettings = async (id) => {
    try {
      const response = await apiClient.get(`/v1/admin/settings/${id}`);
      setSettings(response.data);
      
      // Set logo preview if logo exists
      if (response.data.logo_url) {
        setLogoPreview(response.data.logo_url);
      } else if (response.data.logo_filename) {
        setLogoPreview(`${process.env.REACT_APP_BUNNY_CDN}logos/${response.data.logo_filename}`);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Failed to load hostel settings' });
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, or SVG');
      return;
    }

    // Validate file size (300KB)
    if (file.size > 300 * 1024) {
      toast.error(`File too large. Maximum size: 300KB (Your file: ${(file.size / 1024).toFixed(1)}KB)`);
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error('Please select a logo file first');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);

      const response = await apiClient.post(`/v1/admin/settings/${hostelId}/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('✅ Logo uploaded successfully!');
      setSettings(prev => ({ 
        ...prev, 
        logo_filename: response.data.logo_filename,
        logo_url: response.data.logo_url 
      }));
      if (response.data.logo_url) {
        setLogoPreview(response.data.logo_url);
      }
      setLogoFile(null);
      
      // Trigger settings reload in other components
      window.dispatchEvent(new Event('hostel-settings-updated'));
      
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = async () => {
    if (!hostelId) {
      setMessage({ type: 'error', text: 'Hostel ID not found' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await apiClient.put(`/v1/admin/settings/${hostelId}`, settings);
      setMessage({ type: 'success', text: 'Settings updated successfully!' });
      toast.success('✅ Settings updated successfully!');
      
      // Trigger settings reload in other components
      window.dispatchEvent(new Event('hostel-settings-updated'));
      
      // Refresh settings from backend
      fetchSettings(hostelId);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update settings' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Hostel Settings</h1>

      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Logo Upload */}
        <div>
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Hostel Logo</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-6">
              {/* Logo Preview */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain rounded-lg" />
                  ) : (
                    <ImageIcon className="w-12 h-12 text-gray-400" />
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">Square ratio<br/>recommended</p>
              </div>

              {/* Upload Controls */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Logo (Max 300KB, PNG/JPG/SVG)
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                      <Upload className="w-5 h-5 text-gray-600" />
                      <span className="text-sm text-gray-700">
                        {logoFile ? logoFile.name : 'Choose logo file'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </label>
                  <button
                    onClick={handleLogoUpload}
                    disabled={!logoFile || uploadingLogo}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                  >
                    {uploadingLogo ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  This logo will appear on your dashboard and as the favicon. Square images (200x200 or 512x512) work best.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hostel Name *
              </label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={settings.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter hostel name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Address *
              </label>
              <textarea
                className="w-full p-2 border border-gray-300 rounded-lg"
                rows="3"
                value={settings.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Enter complete hostel address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={settings.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="e.g., Mumbai"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State *
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={settings.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="e.g., Maharashtra"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Information *
              </label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={settings.contact_info}
                onChange={(e) => handleChange('contact_info', e.target.value)}
                placeholder="Phone number or email"
              />
            </div>
          </div>
        </div>

        {/* Student ID Configuration */}
        <div>
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Student ID Configuration</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Student ID Prefix
              </label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={settings.student_id_prefix}
                onChange={(e) => handleChange('student_id_prefix', e.target.value.toUpperCase())}
                placeholder="e.g., STU, DORM"
                maxLength="6"
              />
              <p className="mt-1 text-sm text-gray-500">
                Preview: {settings.student_id_prefix}-{settings.id_start_number}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Starting Number
              </label>
              <input
                type="number"
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={settings.id_start_number}
                onChange={(e) => handleChange('id_start_number', parseInt(e.target.value))}
                min="1"
              />
              <p className="mt-1 text-sm text-gray-500">
                New students will start from this number
              </p>
            </div>
          </div>
        </div>

        {/* Branding */}
        <div>
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Branding</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hostel Logo URL (Optional)
            </label>
            <input
              type="url"
              className="w-full p-2 border border-gray-300 rounded-lg"
              value={settings.hostel_logo_url || ''}
              onChange={(e) => handleChange('hostel_logo_url', e.target.value)}
              placeholder="https://example.com/logo.png"
            />
            {settings.hostel_logo_url && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Logo Preview:</p>
                <img 
                  src={settings.hostel_logo_url} 
                  alt="Hostel Logo" 
                  className="h-20 w-auto object-contain border rounded"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-4 border-t">
          <button
            onClick={handleSave}
            disabled={loading}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="mt-6 bg-yellow-50 rounded-lg p-4">
        <h3 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes</h3>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• Student ID prefix and starting number affect new student registrations</li>
          <li>• Existing student IDs will not be changed</li>
          <li>• Logo URL should be publicly accessible</li>
          <li>• Changes take effect immediately after saving</li>
        </ul>
      </div>
    </div>
  );
};

export default HostelSettings;